import { getWorldDraftCatalog } from './placeTreeWorldSave.js';
import { normalizeShapeTypeName, getShapePoints, getBoxShapePoints } from './placeOverviewMap.js';

const finite = value => value !== null && value !== undefined && value !== '' && Number.isFinite(Number(value));
const parse = value => { try { return typeof value === 'string' ? JSON.parse(value) : value || {}; } catch { return {}; } };
export const getPreviewFootprints = place => {
  const shapes = place.shapes?.length ? place.shapes : place.shape ? [place.shape] : [];
  const parts = shapes.flatMap(shape => {
    const data = parse(shape.shapeData);
    return normalizeShapeTypeName(shape.shapeType) === 'path' && Array.isArray(data.paths)
      ? data.paths.map(points => ({ ...shape, shapeData: { width: data.width, points } })) : [shape];
  });
  const footprints = parts.map(shape => {
    const type = normalizeShapeTypeName(shape.shapeType);
    const data = parse(shape.shapeData);
    let points = getShapePoints({ shape });
    if (['polygon', 'path'].includes(type) && points.length >= (type === 'path' ? 2 : 3)) return { type, points, width: type === 'path' ? Number(data.width) || 20 : 0 };
    if (!['minX', 'maxX', 'minZ', 'maxZ'].every(key => finite(shape[key]))) return null;
    const { minX, maxX, minZ, maxZ } = Object.fromEntries(['minX', 'maxX', 'minZ', 'maxZ'].map(key => [key, Number(shape[key])]));
    if (minX > maxX || minZ > maxZ) return null;
    if (['sphere', 'cylinder'].includes(type)) points = Array.from({ length: 64 }, (_, i) => ({
      x: (minX + maxX) / 2 + (maxX - minX) / 2 * Math.cos(i * Math.PI / 32), z: (minZ + maxZ) / 2 + (maxZ - minZ) / 2 * Math.sin(i * Math.PI / 32) }));
    else points = getBoxShapePoints({ shape });
    return { type: 'polygon', points, width: 0 };
  }).filter(Boolean);
  if (footprints.length) return footprints;
  const location = place.plannedLocation || place.location;
  if (!finite(location?.x) || !finite(location?.z)) return [];
  return [{ type: 'point', points: [{ x: Number(location.x), z: Number(location.z) }], width: 0 }];
};
export const previewBounds = footprints => {
  const points = footprints.flatMap(shape => shape.points.map(p => ({ ...p, padding: (shape.width || 0) / 2 })));
  if (!points.length) return null;
  return { minX: Math.min(...points.map(p => p.x - p.padding)), maxX: Math.max(...points.map(p => p.x + p.padding)),
    minZ: Math.min(...points.map(p => p.z - p.padding)), maxZ: Math.max(...points.map(p => p.z + p.padding)) };
};

export const getPreviewRouteLabel = (entry, bounds) => {
  // Clip a real segment to the visible map before choosing a label position;
  // world-spanning route endpoints are usually far outside the viewport.
  const segments = entry.shapes.filter(shape => shape.type === 'path').flatMap(shape => shape.points.slice(1).map((to, i) => {
    const from = shape.points[i], dx = to.x - from.x, dz = to.z - from.z;
    let start = 0, end = 1;
    for (const [p, q] of [[-dx, from.x - bounds.minX], [dx, bounds.maxX - from.x], [-dz, from.z - bounds.minZ], [dz, bounds.maxZ - from.z]]) {
      if (p === 0) { if (q < 0) return null; }
      else if (p < 0) start = Math.max(start, q / p);
      else end = Math.min(end, q / p);
      if (start > end) return null;
    }
    return { x: from.x + dx * (start + (end - start) * .35), z: from.z + dz * (start + (end - start) * .35),
      length: Math.hypot(dx, dz) * (end - start) };
  })).filter(Boolean).sort((a, b) => b.length - a.length);
  return segments[0] || null;
};
export const getSpatialPreviewEntries = (draft, development) => {
  const entries = new Map();
  const add = (place, id, status, diagnostics) => {
    if (!place) return;
    const shapes = getPreviewFootprints(place);
    if (!shapes.length) return;
    entries.set(id, { id, place, name: place.name || place.entityName || id, status, shapes, bounds: previewBounds(shapes), diagnostics });
  };
  const root = draft.draftPlace || {};
  const context = [...(root.siblingPlaces || []), ...(root.adjacentPlaces || []), ...(root.composedOfParents || []), ...(root.composedOfChildren || []), ...(development.savedNearbyPlaces || [])];
  context.forEach((place, i) => add(place, place.idEntity || place.draftPlaceId || `context:${i}`, 'context'));
  for (const feature of development.mapContext?.features || []) {
    // Older drafts contain saved place identities without their footprints. Their
    // point fallback must not hide the clipped road/river geometry already collected.
    if (entries.get(feature.placeId)?.shapes.some(shape => shape.type !== 'point')) continue;
    const shapes = (feature.geometries || []).flatMap(geometry => geometry.polygon ? [{ shapeType: 'Polygon', shapeData: { points: geometry.polygon } }] :
      (geometry.segments || []).map(segment => ({ shapeType: 'Path', shapeData: { points: [segment.from, segment.to], width: geometry.widthFeet || 1 } })));
    add({ ...entries.get(feature.placeId)?.place, name: feature.name, shapes, shape: shapes[0], mapRole: feature.role,
      approximate: feature.geometryTruncated || feature.geometries?.some(g => g.approximate || g.geometryTruncated) }, feature.placeId, 'context');
  }
  for (const [id, diagnostics] of Object.entries(development.spatialDiagnostics || {})) {
    (diagnostics.context || []).forEach((place, i) => {
      const key = place.idEntity || place.draftPlaceId || `diagnostic-context:${place.name || i}`;
      if (![...entries.values()].some(entry => entry.name === place.name)) add(place, key, 'context');
    });
    if (diagnostics.parent?.name !== (root.name || root.entityName) && ![...entries.values()].some(entry => entry.name === diagnostics.parent?.name)) add(diagnostics.parent, `parent:${id}`, 'context');
    diagnostics.attempts?.forEach((attempt, i) => {
      if (attempt.rejected) add(attempt.rejected, `${id}:rejected:${i}`, 'rejected', { ...attempt, draftId: id });
      if (attempt.corrected) add(attempt.corrected, `${id}:corrected:${i}`, 'corrected', { ...attempt, draftId: id });
      if (attempt.generated && !attempt.accepted && !attempt.rejected) add(attempt.generated, `${id}:candidate:${i}`, 'candidate', { ...attempt, draftId: id });
    });
  }
  for (const entry of getWorldDraftCatalog({ ...draft, worldDevelopment: development }).filter(entry => entry.kind === 'Place')) {
    const prepared = development.finalRecords?.[entry.draftId];
    const saved = entry.entity.savedPlaceId || draft.worldSaveProgress?.savedIds?.[entry.draftId];
    add(entry.entity, entry.draftId, saved ? 'saved' : prepared || entry.draftId === (root.draftPlaceId || 'draft-place:root') ? 'accepted' : 'planned', development.spatialDiagnostics?.[entry.draftId]);
  }
  return [...entries.values()];
};
